import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { SimpleQuoteProvider } from "@/contexts/SimpleQuoteContext";
import { SimpleQuoteManagerHeader } from "@/components/admin/quotes/SimpleQuoteManagerHeader";
import { QuoteManagerFilters } from "@/components/admin/quotes/QuoteManagerFilters";
import { QuoteList } from "@/components/admin/quotes/QuoteList";
import { SimpleTestQuoteSender } from "@/components/admin/quotes/SimpleTestQuoteSender";
import { InlineQuoteForm } from "@/components/admin/quotes/InlineQuoteForm";
import { ImportQuotesTab } from "@/components/admin/quotes/ImportQuotesTab";
import { useSimpleQuoteContext } from "@/contexts/SimpleQuoteContext";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { QueryClientMonitor } from "@/debug/QueryClientMonitor";
import { Command, CommandGroup } from "@/components/ui/command";
import { CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useQuotes } from "@/hooks/useQuotes";
import { SortField, SortOrder } from "@/types/quote";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function QuoteManagerContent() {
  const {
    editMode,
    currentQuote,
    cancelEdit,
    selectedQuotes,
    handleQuoteSelect,
    handleQuoteDeselect,
    startEditQuote,
    startCreateQuote,
    handleQuoteDelete,
    searchQuery,
    categoryFilter,
    filterUncategorized,
    showDuplicates,
    pageSize
  } = useSimpleQuoteContext();
  
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("list");
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("content");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { quotes, isLoading, totalPages } = useQuotes({
    page: currentPage,
    sortField,
    sortOrder,
    searchQuery,
    pageSize,
    categoryId: categoryFilter === 'all' ? undefined : categoryFilter,
    showUncategorized: filterUncategorized,
    showDuplicates: showDuplicates
  });

  const handleSort = (field: SortField, order: SortOrder) => {
    setSortField(field);
    setSortOrder(order);
  };

  const handleSelectQuote = (id: string, checked: boolean) => {
    if (checked) {
      handleQuoteSelect(id);
    } else {
      handleQuoteDeselect(id);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      quotes.forEach(quote => handleQuoteSelect(quote.id));
    } else {
      quotes.forEach(quote => handleQuoteDeselect(quote.id));
    }
  };

  const handleEdit = (quote: any) => {
    startEditQuote(quote);
    setActiveTab("list");
  };

  const handleAdd = () => {
    startCreateQuote();
    setActiveTab("list");
  };

  const handleDelete = (quote: any) => {
    setQuoteToDelete(quote);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quoteToDelete) return;

    setIsSubmitting(true);
    try {
      handleQuoteDelete(quoteToDelete.id);
      setIsDeleteDialogOpen(false);
      setQuoteToDelete(null);
      setSuccessMessage('Quote deleted successfully');
      setIsSuccessDialogOpen(true);
    } catch (err) {
      setErrorMessage('Failed to delete quote');
      setIsErrorDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportCurrentQuotes = () => {
    toast({
      title: "Stubbed export",
      description: "This is a stub export handler.",
    });
  };

  const handleExportAllQuotes = async () => {
    toast({
      title: "Stubbed export all",
      description: "This is a stub for export all.",
    });
  };

  const handleExportByCategory = async (categoryId: string) => {
    toast({
      title: "Stubbed export by category",
      description: `This is a stub for category: ${categoryId}`,
    });
  };

  useEffect(() => {
    return () => {
      queryClient?.getMutationCache().clear();
    };
  }, [queryClient]);

  return (
    <>
      <div className="space-y-6">
        <SimpleQuoteManagerHeader 
          onExportCurrent={handleExportCurrentQuotes}
          onExportAll={handleExportAllQuotes}
          onExportByCategory={handleExportByCategory}
          isExporting={isExporting}
          onAddQuote={handleAdd}
        />

        <Dialog open={editMode !== 'none'} onOpenChange={(open) => !open && cancelEdit()}>
          <DialogContent className="sm:max-w-[700px] w-[95vw] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode === 'edit' ? 'Edit Quote' : 'Add New Quote'}
              </DialogTitle>
              <DialogDescription>
                {editMode === 'edit' 
                  ? 'Modify the quote details below.'
                  : 'Create a new quote by filling in the details below.'}
              </DialogDescription>
            </DialogHeader>
            <InlineQuoteForm 
              quote={currentQuote} 
              onCancel={cancelEdit}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Quote Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Quote</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this quote? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setQuoteToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={isSubmitting}
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Dialog */}
        <Dialog open={isSuccessDialogOpen} onOpenChange={setIsSuccessDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Success</DialogTitle>
              <DialogDescription>{successMessage}</DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        {/* Error Dialog */}
        <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Error</DialogTitle>
              <DialogDescription>{errorMessage}</DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="list">Quote List</TabsTrigger>
            <TabsTrigger value="import">Import Quotes</TabsTrigger>
            <TabsTrigger value="test">Send Test Quote</TabsTrigger>
          </TabsList>
          
          <TabsContent value="list" className="mt-4">
            <Card className="p-4">
              <QuoteManagerFilters />
              <div className="mt-4">
                <QuoteList
                  quotes={quotes || []}
                  selectedQuotes={selectedQuotes}
                  onSelectQuote={handleSelectQuote}
                  onSelectAll={handleSelectAll}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  showDuplicateHighlight={true}
                />
              </div>
            </Card>
          </TabsContent>
          
          <TabsContent value="import" className="mt-4">
            <ImportQuotesTab />
          </TabsContent>
          
          <TabsContent value="test" className="mt-4">
            <SimpleTestQuoteSender />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// The main QuoteManager component wraps everything in both QueryClientProvider AND SimpleQuoteProvider
const SimpleQuoteManager = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SimpleQuoteProvider>
          <QuoteManagerContent />
        </SimpleQuoteProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default SimpleQuoteManager;
